import React, {useCallback} from 'react';
import {useEditor, EditorContent} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import {TextStyle} from '@tiptap/extension-text-style';
import {Color} from '@tiptap/extension-color';
import {Link} from '@tiptap/extension-link';
import i18n from 'i18n';

const sharedExtensions = [
    // Disable built-in link extension from StarterKit to avoid duplicate 'link' extensions
    // since we register a custom-configured Link extension below.
    StarterKit.configure({
        link: false,
    }),
    TextAlign.configure({
        types: ['heading', 'paragraph'],
    }),
    TextStyle,
    Color,
    Link.configure({
        openOnClick: false,
    }),
];

const TEXT_COLORS = {
    red: '#ef4444',
    blue: '#009efb',
    green: '#10b981',
};

interface Props {
    onChange: (htmlContent: string) => void;
    initialValue?: string;
    value?: string;
    customHeight?: object;
    placeholder?: string;
    simple?: boolean; // New prop to control UI complexity
}

type RichTextEditorStyle = React.CSSProperties & {
    '--rich-text-editor-color-red'?: string;
    '--rich-text-editor-color-blue'?: string;
    '--rich-text-editor-color-green'?: string;
};

const editorHasDocumentContent = (editor: any): boolean => {
    if (!editor) {
        return false;
    }

    if (!editor.isEmpty) {
        return true;
    }

    const hasStructuralContent = (node: any): boolean => {
        if (!node) {
            return false;
        }

        if (node.type && !['doc', 'paragraph', 'text'].includes(node.type)) {
            return true;
        }

        return Array.isArray(node.content) && node.content.some(hasStructuralContent);
    };

    return hasStructuralContent(editor.getJSON());
};

const ToolbarButton = ({ 
    onClick, 
    isActive = false, 
    disabled = false, 
    children, 
    title 
}: {
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    title?: string;
}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={title}
        data-cy="rich-text-editor-btn"
        className={`rich-text-editor-button ${isActive ? 'rich-text-editor-button-active' : ''}`}
    >
        {children}
    </button>
);

const Separator = () => (
    <div className="rich-text-editor-separator" />
);

const SimpleMenuBar = ({editor}: { editor: any }) => {
    if (!editor) {return null;}

    return (
        <div className="rich-text-editor-toolbar">
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                isActive={editor.isActive('bold')}
                disabled={!editor.can().chain().focus().toggleBold().run()}
                title={i18n.t('jt_bold_ctrl_b.text')}
            >
                <strong>B</strong>
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                isActive={editor.isActive('italic')}
                disabled={!editor.can().chain().focus().toggleItalic().run()}
                title={i18n.t('jt_italic_ctrl_i.text')}
            >
                <em>I</em>
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                isActive={editor.isActive('strike')}
                disabled={!editor.can().chain().focus().toggleStrike().run()}
                title={i18n.t('jt_strikethrough.text')}
            >
                <s>S</s>
            </ToolbarButton>

            <Separator />

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                isActive={editor.isActive('bulletList')}
                title={i18n.t('jt_bullet_list.text')}
            >
                •
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                isActive={editor.isActive('orderedList')}
                title={i18n.t('jt_numbered_list.text')}
            >
                1.
            </ToolbarButton>

            <Separator />

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                isActive={editor.isActive('blockquote')}
                title={i18n.t('jt_quote.text')}
            >
                "
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                title={i18n.t('jt_horizontal_rule.text')}
            >
                ―
            </ToolbarButton>

            <Separator />

            <ToolbarButton
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().chain().focus().undo().run()}
                title={i18n.t('jt_undo_ctrl_z.text')}
            >
                ↶
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().chain().focus().redo().run()}
                title={i18n.t('jt_redo_ctrl_y.text')}
            >
                ↷
            </ToolbarButton>
        </div>
    );
};

const AdvancedMenuBar = ({editor}: { editor: any }) => {
    if (!editor) {return null;}

    const addLink = () => {
        const url = window.prompt('Enter URL:');
        if (url) {
            editor.chain().focus().setLink({href: url}).run();
        }
    };

    return (
        <div className="rich-text-editor-toolbar">
            {/* Text Formatting */}
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                isActive={editor.isActive('bold')}
                disabled={!editor.can().chain().focus().toggleBold().run()}
                title={i18n.t('jt_bold_ctrl_b.text')}
            >
                <strong>B</strong>
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                isActive={editor.isActive('italic')}
                disabled={!editor.can().chain().focus().toggleItalic().run()}
                title={i18n.t('jt_italic_ctrl_i.text')}
            >
                <em>I</em>
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                isActive={editor.isActive('strike')}
                disabled={!editor.can().chain().focus().toggleStrike().run()}
                title={i18n.t('jt_strikethrough.text')}
            >
                <s>S</s>
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleCode().run()}
                isActive={editor.isActive('code')}
                disabled={!editor.can().chain().focus().toggleCode().run()}
                title={i18n.t('jt_inline_code.text')}
            >
                &lt;/&gt;
            </ToolbarButton>

            <Separator />

            {/* Headings */}
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleHeading({level: 1}).run()}
                isActive={editor.isActive('heading', {level: 1})}
                title={i18n.t('jt_heading_1.text')}
            >
                H1
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleHeading({level: 2}).run()}
                isActive={editor.isActive('heading', {level: 2})}
                title={i18n.t('jt_heading_2.text')}
            >
                H2
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleHeading({level: 3}).run()}
                isActive={editor.isActive('heading', {level: 3})}
                title={i18n.t('jt_heading_3.text')}
            >
                H3
            </ToolbarButton>

            <Separator />

            {/* Text Alignment */}
            <ToolbarButton
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                isActive={editor.isActive({textAlign: 'left'})}
                title={i18n.t('jt_align_left.text')}
            >
                ⬅
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                isActive={editor.isActive({textAlign: 'center'})}
                title={i18n.t('jt_align_center.text')}
            >
                ↔
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                isActive={editor.isActive({textAlign: 'right'})}
                title={i18n.t('jt_align_right.text')}
            >
                ➡
            </ToolbarButton>

            <Separator />

            {/* Lists */}
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                isActive={editor.isActive('bulletList')}
                title={i18n.t('jt_bullet_list.text')}
            >
                •
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                isActive={editor.isActive('orderedList')}
                title={i18n.t('jt_numbered_list.text')}
            >
                1.
            </ToolbarButton>

            <Separator />

            {/* Links */}
            <ToolbarButton
                onClick={addLink}
                isActive={editor.isActive('link')}
                title={i18n.t('jt_add_link.text')}
            >
                🔗
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().unsetLink().run()}
                disabled={!editor.isActive('link')}
                title={i18n.t('jt_remove_link.text')}
            >
                🔗❌
            </ToolbarButton>

            <Separator />

            {/* Colors */}
            <ToolbarButton
                onClick={() => editor.chain().focus().setColor(TEXT_COLORS.red).run()}
                isActive={editor.isActive('textStyle', {color: TEXT_COLORS.red})}
                title={i18n.t('jt_red_text.text')}
            >
                <span className="rich-text-editor-color-preview" data-color="red">A</span>
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().setColor(TEXT_COLORS.blue).run()}
                isActive={editor.isActive('textStyle', {color: TEXT_COLORS.blue})}
                title={i18n.t('jt_blue_text.text')}
            >
                <span className="rich-text-editor-color-preview" data-color="blue">A</span>
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().setColor(TEXT_COLORS.green).run()}
                isActive={editor.isActive('textStyle', {color: TEXT_COLORS.green})}
                title={i18n.t('jt_green_text.text')}
            >
                <span className="rich-text-editor-color-preview" data-color="green">A</span>
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().unsetColor().run()}
                title={i18n.t('jt_remove_color.text')}
            >
                A
            </ToolbarButton>

            <Separator />

            {/* Block Elements */}
            <ToolbarButton
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                isActive={editor.isActive('blockquote')}
                title={i18n.t('jt_quote.text')}
            >
                "
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                title={i18n.t('jt_horizontal_rule.text')}
            >
                ―
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().setHardBreak().run()}
                title={i18n.t('jt_line_break.text')}
            >
                ↵
            </ToolbarButton>

            <Separator />

            {/* Undo/Redo */}
            <ToolbarButton
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().chain().focus().undo().run()}
                title={i18n.t('jt_undo_ctrl_z.text')}
            >
                ↶
            </ToolbarButton>

            <ToolbarButton
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().chain().focus().redo().run()}
                title={i18n.t('jt_redo_ctrl_y.text')}
            >
                ↷
            </ToolbarButton>
        </div>
    );
};

const RichTextEditor = React.memo<Props>(({
    onChange,
    initialValue,
    value,
    customHeight = {},
    placeholder = 'Write something here...',
    simple = false, // Default to advanced mode
}) => {
    const [hasContent, setHasContent] = React.useState(false);

    if (value === undefined && initialValue === undefined) {
        throw Error('RichTextEditor requires either `initialValue` or `value` to be passed in as a prop');
    }

    const handleUpdate = useCallback(({editor}: { editor: any }) => {
        const html = editor.getHTML();
        setHasContent(editorHasDocumentContent(editor));
        onChange(html);
    }, [onChange]);

    const editor = useEditor({
        extensions: sharedExtensions,
        content: value !== undefined ? value : initialValue || '',
        onUpdate: handleUpdate,
        editorProps: {
            attributes: {
                class: 'rich-text-editor-editor',
            },
        },
    });

    React.useEffect(() => {
        if (editor && value !== undefined && editor.getHTML() !== value) {
            editor.commands.setContent(value);
        }
        if (editor) {
            setHasContent(editorHasDocumentContent(editor));
        }
    }, [editor, value]);

    const wrapperStyle: RichTextEditorStyle = {};
    if (customHeight) {
        Object.entries(customHeight).forEach(([key, val]) => {
            if (typeof val === 'string' && val.includes('calc(') && !val.includes('px') && !val.includes('%') && !val.includes('rem')) {
                return;
            }
            wrapperStyle[key as keyof React.CSSProperties] = val;
        });
    }
    wrapperStyle['--rich-text-editor-color-red'] = TEXT_COLORS.red;
    wrapperStyle['--rich-text-editor-color-blue'] = TEXT_COLORS.blue;
    wrapperStyle['--rich-text-editor-color-green'] = TEXT_COLORS.green;

    return (
        <div className="rich-text-editor-wrapper" style={wrapperStyle}>
            {simple ? (
                <SimpleMenuBar editor={editor} />
            ) : (
                <AdvancedMenuBar editor={editor} />
            )}
            
            <div className="rich-text-editor-content-wrapper">
                <EditorContent 
                    editor={editor} 
                    className="rich-text-editor-content"
                />
                {!hasContent && (
                    <div className="rich-text-editor-placeholder">
                        {placeholder}
                    </div>
                )}
            </div>
        </div>
    );
});

export default RichTextEditor;